<!--
  Note : this shows the rota in an agenda view, particular to an individual user.
-->
<template>
  <div id="rota" class="agenda-view">
    <div
      v-if="daysBeforeToday.length > 0"
      id="previousShifts"
      title="Previous shifts"
      class="collapse"
      style="clear: both"
    >
      <template v-for="(dayIndex, x) in daysBeforeToday" :key="x">
        <rota-user-agenda-view-day
          :rota="rota"
          :rota-shifts-for-user-or-nobody="rotaShiftsForUserOrNobody"
          :rota-users="rotaUsers"
          :activities="activities"
          :activities-not-assigned-without-support="
            activitiesNotAssignedWithoutSupport
          "
          :rota-agencies="rotaAgencies"
          :day-index="dayIndex"
          :oncall="oncall"
          :location-clients="locationClients"
          @rota-shift-self-assign="$emit('rota-shift-self-assign', $event)"
        />
      </template>
    </div>
    <div v-if="daysBeforeToday.length > 0" class="previous-days-toggle">
      <button
        class="btn btn-sm btn-light"
        type="button"
        data-bs-toggle="collapse"
        data-bs-target="#previousShifts"
        aria-expanded="false"
        aria-controls="previousShifts"
      >
        Show Previous Days
      </button>
    </div>
    <template v-for="(dayIndex, x) in daysTodayOnwards" :key="x">
      <rota-user-agenda-view-day
        :rota="rota"
        :rota-shifts-for-user-or-nobody="rotaShiftsForUserOrNobody"
        :rota-users="rotaUsers"
        :activities="activities"
        :activities-not-assigned-without-support="
          activitiesNotAssignedWithoutSupport
        "
        :rota-agencies="rotaAgencies"
        :day-index="dayIndex"
        :oncall="oncall"
        :location-clients="locationClients"
        @rota-shift-self-assign="$emit('rota-shift-self-assign', $event)"
      />
    </template>
  </div>
</template>

<script>
import { computed, ref } from "vue";
import { mapGetters, useStore } from "vuex";
import { eachDayOfInterval, isFuture, isToday, subDays } from "date-fns";
import { useAuthStore } from "@switchsystems/coactive-vue-lib";
import RotaUserAgendaViewDay from "@/components/rota/agenda/RotaUserAgendaViewDay.vue";

import sessions from "../../../lib/rota/sessions";
import {
  getActivitiesWithoutSupport,
  getActivitiesNotAssignedInRota,
} from "../../../lib/rota/activity-functions";
import {
  filterShiftsForUser,
  filterShiftsNotCurrentlyAssigned,
  filterCurrentShiftsForAgencyId,
} from "../../../lib/rota/shift-functions";

export default {
  name: "RotaUserAgendaView",
  components: {
    RotaUserAgendaViewDay,
  },
  props: {
    rota: {
      type: Object,
      default() {
        return {};
      },
    },
    activities: {
      type: [Boolean, Array],
      default() {
        return [];
      },
    },
  },
  emits: ["rota-shift-self-assign"],
  setup(props) {
    const store = useStore();
    const authStore = useAuthStore();

    const rotaAgencies = ref([]);
    store
      .dispatch("rotaagencies/getRotaAgenciesForAgencySelection")
      .then((data) => {
        if (data !== false) {
          rotaAgencies.value = data;
        }
      });

    const locationClients = ref([]);
    store
      .dispatch(
        "clients/getClientsAtLocationForClientSelection",
        props.rota.location,
      )
      .then((data) => {
        if (data !== false) {
          locationClients.value = data;
        }
      });

    const oncall = ref(false);
    store
      .dispatch("rotaoncall/getAllRotaOncalls", {
        filter: [
          {
            subject: "location",
            comparison: "eq",
            value: props.rota.location,
          },
          {
            subject: "date",
            comparison: "between-inc",
            value: [props.rota.startDate, props.rota.endDate],
          },
        ],
      })
      .then((data) => {
        if (data !== false) {
          oncall.value = data._embedded["rota-oncall"];
        }
      });

    const rotaUsers = ref([]);
    store.dispatch("rotausers/getRotaUsersForUserSelection").then((data) => {
      if (data !== false) {
        rotaUsers.value = data;
      }
    });

    const rotaShiftsForUserOrNobody = computed(() => {
      // filter the shifts array down to the current user or nobody,
      // to reduce the number of shifts we're passing around and iterating over
      const userId = authStore.isAgencyUser
        ? authStore.user.agencyId
        : authStore.user.id;

      const uniqueShifts = new Map();

      filterShiftsNotCurrentlyAssigned(props.rota.shifts).forEach((shift) => {
        uniqueShifts.set(shift.id, shift);
      });

      if (authStore.isAgencyUser) {
        filterCurrentShiftsForAgencyId(
          props.rota.shifts,
          authStore.user.agencyId,
        ).forEach((shift) => {
          uniqueShifts.set(shift.id, shift);
        });
      }

      filterShiftsForUser(props.rota.shifts, userId).forEach((shift) => {
        uniqueShifts.set(shift.id, shift);
      });

      // convert the map back to an array
      return Array.from(uniqueShifts.values());
    });

    return {
      sessions,
      rotaAgencies,
      locationClients,
      rotaUsers,
      oncall,
      getActivitiesWithoutSupport,
      getActivitiesNotAssignedInRota,
      rotaShiftsForUserOrNobody,
    };
  },
  data() {
    return {};
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
      locations: "locations",
    }),
    daysBeforeToday() {
      let dayIndex = 0;
      const daysBeforeToday = [];
      eachDayOfInterval({
        start: this.rota.startDate,
        end: subDays(this.rota.endDate, 1),
      }).forEach((day) => {
        // nb: don't use isPast() because it doesn't work when we get after the midnight day start
        if (isToday(day) || isFuture(day)) {
          // do nothing
        } else {
          daysBeforeToday.push(dayIndex);
        }
        dayIndex += 1;
      });
      return daysBeforeToday;
    },
    daysTodayOnwards() {
      let dayIndex = 0;
      const daysTodayOnwards = [];
      eachDayOfInterval({
        start: this.rota.startDate,
        end: subDays(this.rota.endDate, 1),
      }).forEach((day) => {
        // nb:: don't use !isPast() because it doesn't work when we get after the midnight day start
        if (isToday(day) || isFuture(day)) {
          daysTodayOnwards.push(dayIndex);
        }
        dayIndex += 1;
      });
      return daysTodayOnwards;
    },
    activitiesNotAssignedWithoutSupport() {
      if (this.activities === false) {
        return [];
      }
      const activitiesNotAssignedInRota = getActivitiesNotAssignedInRota(
        this.activities,
        this.rota,
      );
      return getActivitiesWithoutSupport(activitiesNotAssignedInRota);
    },
  },
};
</script>

<style lang="scss">
#home {
  #top-nav {
    position: sticky;
    top: 0px;
    z-index: 3;
  }
  #top-nav > nav {
    position: relative;
  }
  .page-body {
    margin-top: 0px;
    padding-top: 0.5em;
  }
  div.day .datetime {
    position: sticky;
    top: $navheight;
    z-index: 2;
    padding-top: 0.5em;
    padding-bottom: 0.5em;
    padding-right: 0.25em;
    text-decoration: underline;
    text-decoration-skip-ink: none;
    text-decoration-thickness: 10%;
    span.shift-time {
      &:not(.no-current) {
        text-decoration: underline;
        text-decoration-skip-ink: none;
        text-decoration-thickness: 10%;
      }
      float: right;
    }
  }
}
@include media-breakpoint-down(sm) {
  #home {
    #rota.agenda-view {
      div.day {
        .datetime {
          top: $navheight-mobile;
          font-size: 1rem;
        }

        padding: 0em 0.25em 0.25em 0.35em;
      }
    }
  }
}

@include media-breakpoint-down(md) {
  #home {
    #rota.agenda-view {
      div.day {
        div.unassigned-activities {
          border-radius: 0;
          padding: 0.5em;
          border-top: 1px dashed #999;
          padding-top: 0.75em;
          margin-top: 0.25em;
          padding-left: 0;
          margin-left: 0;
          border-left: none;
          height: auto;
        }
      }
    }
  }
}

#rota.agenda-view {
  --day-background: #f0f0f0;
  h3 {
    font-size: 1.1em;
  }
  div.day {
    margin-bottom: 2.5em;
    padding: 0.25em 0.5em 0.5em 0.85em;
    background: var(--day-background);
    border-left: 2px solid #ccc;
    border-radius: 4px;
    .datetime {
      padding-left: 0;
      font-weight: bold;
      font-size: 1.2rem;
      background-color: var(--day-background); // required for sticky header
    }
    .oncall {
      padding-left: 0;
      padding-bottom: 0.5em;
      font-size: 1.2rem;
    }
    p.no-current {
      padding-left: 0;
      display: none;
    }
    div.unassigned-activities {
      border-radius: 0;
      padding: 0.5em;
      border-left: 1px dashed #999;
      padding-left: 0.75em;
      margin-left: 0.25em;
      height: 100%;
    }
    .activities span.placeholder {
      background: $pink-100;
    }
    div.shift {
      border: 2px solid $gray-600;
      border-radius: 0.5em;
      margin-bottom: 0.25em;
      &.type-nobody {
        a {
          color: #333 !important;
        }
        .card {
          --bs-card-bg: inherit;
        }
      }
    }
    &.today {
      border-left: 4px solid #0a58ca;
      background: $blue-100;
      .datetime {
        background-color: $blue-100; // required for sticky header
      }
      div.shift {
        border-color: #0a58ca;
        background: var(--bs-body-bg);

        &.type-holiday > div,
        &.type-sick > div {
          background: repeating-linear-gradient(
            135deg,
            #abb3e744,
            #abb3e744 10px,
            #c7cffa44 10px,
            #c7cffa44 20px
          );
        }
        &.type-nobody {
          background-color: $purple-100;
        }
      }
    }
    &.holiday-or-sick {
      .type-holiday,
      .type-sick {
        background: repeating-linear-gradient(
          135deg,
          #abb3e733,
          #abb3e733 10px,
          #c7cffa33 10px,
          #c7cffa33 20px
        );
        border-radius: 0.5em;
      }
    }
    &.no-current {
      p.no-current {
        display: block;
      }
    }
  }
  .rotashift-agenda > .time {
    font-size: 1.2em;
  }
  .time {
    font-weight: bold;
  }
  div.activity {
    .time {
      font-weight: normal;
    }
  }
  .notes {
    color: #f00;
  }
  div.shift {
    margin-left: 0;
    padding-right: 0px;
    padding-left: 0;
    padding-top: 0px;
    > div {
      padding: 0.5em;
      .title {
        padding-bottom: 0px;
        font-weight: bold;
      }
    }
    &.type-nobody {
      margin-top: 0.5em;
      margin-bottom: 0.5em;
      background-color: $purple-200;
    }
  }

  .previous-days-toggle {
    margin-bottom: 0.5em;
    display: flex;
    flex-flow: nowrap;
    justify-content: space-between;
    align-items: center;
    &::before,
    &::after {
      flex-grow: 1;
      flex-shrink: 1;
      flex-basis: auto;
      content: " ";
      height: 0px;
      border-bottom: 1px dashed #ccc;
    }
  }
  .card {
    --bs-card-title-spacer-y: 0;
    h5 {
      margin: 0px;
      a {
        text-decoration: none;
        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
  .unassigned-activities,
  .type-nobody {
    .card {
      --bs-card-spacer-y: 0.25em;
      --bs-card-spacer-x: 0.25em;
      max-height: 8em;
      overflow: hidden;

      .activity-day {
        display: none;
      }
      h5 {
        font-size: 1.1em;
      }

      .note {
        border-top: #00000033 1px solid;
        margin-top: 0.5em;
        opacity: 0.7;
        font-size: 0.9em;
      }
    }
  }
}
</style>
